import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  fmtPct,
  fmtUsd,
  monthlyMortgagePayment,
  parseNum,
  toolInputClass,
} from './toolShared';
import { SITE_GLASS_CARD } from '../../constants/siteUi';

const DEFAULTS = {
  purchase: '',
  units: '1',
  down: '20',
  closing: '2.5',
  capex: '',
  rate: '5',
  term: '30',
  rental: '',
  other: '',
  opex: '',
  vacancy: '5',
  reserve: '250',
};

export function ToolQuickDeal() {
  const [v, setV] = useState(DEFAULTS);

  const out = useMemo(() => {
    const purchase = parseNum(v.purchase);
    const units = Math.max(0, parseNum(v.units));
    const downPct = parseNum(v.down);
    const closingPct = parseNum(v.closing);
    const capex = parseNum(v.capex);
    const rate = parseNum(v.rate);
    const term = parseNum(v.term);
    const rental = parseNum(v.rental);
    const other = parseNum(v.other);
    const opex = parseNum(v.opex);
    const vac = Math.min(100, Math.max(0, parseNum(v.vacancy))) / 100;
    const resU = parseNum(v.reserve);
    if (purchase <= 0 || units <= 0) return null;

    const loan = purchase * (1 - downPct / 100);
    const downCash = purchase * (downPct / 100);
    const closingCash = purchase * (closingPct / 100);
    const totalCash = downCash + closingCash + capex;
    const egi = rental * (1 - vac) + other;
    const reserves = resU * units;
    const noi = egi - opex - reserves;
    const annualDebt = monthlyMortgagePayment(loan, rate, term) * 12;
    const cf = noi - annualDebt;
    const cap = (noi / purchase) * 100;
    const coc = totalCash > 0 ? (cf / totalCash) * 100 : null;
    return { loan, totalCash, egi, noi, annualDebt, cf, cap, coc };
  }, [v]);

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="Quick deal analyzer"
        subtitle="Run a fast sanity check on cash flow, cap rate, and cash-on-cash using annual numbers."
      />

      <InfoSection title="How to use this calculator">
        <p>
          Enter purchase basis, simple financing, and annual income statement lines. Vacancy is applied to{' '}
          <strong className="text-[var(--home-text-on-dark)]">rental income only</strong>; other income is added gross.
          Reserves are annual dollars per unit.
        </p>
        <p className="rounded-xl border border-[var(--home-miami-blue)]/25 bg-[var(--home-miami-blue)]/10 px-4 py-3 text-sm">
          Need AI extraction and a full normalization report? Use the{' '}
          <Link to="/dashboard/analyzer" className="font-semibold text-[var(--home-miami-blue)] underline-offset-4 hover:underline">
            Comprehensive deal analyzer
          </Link>
          .
        </p>
      </InfoSection>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <div className={`${SITE_GLASS_CARD} p-4 md:p-6`}>
            <PanelTitle>Property details</PanelTitle>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="qd-p" tip="Contract price or target basis." required>
                  Purchase price
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="qd-p"
                    type="text"
                    inputMode="decimal"
                    value={v.purchase}
                    onChange={(e) => setV((s) => ({ ...s, purchase: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="qd-u" tip="Unit count." required>
                  Number of units
                </FieldLabel>
                <input
                  id="qd-u"
                  type="text"
                  inputMode="decimal"
                  value={v.units}
                  onChange={(e) => setV((s) => ({ ...s, units: e.target.value }))}
                  className={toolInputClass}
                  placeholder="1"
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="qd-d" tip="Equity as a percent of purchase price." required>
                  Down payment
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="qd-d"
                    type="text"
                    inputMode="decimal"
                    value={v.down}
                    onChange={(e) => setV((s) => ({ ...s, down: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="20.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="qd-cc" tip="Closing costs as a percent of purchase price.">
                  Closing cost %
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="qd-cc"
                    type="text"
                    inputMode="decimal"
                    value={v.closing}
                    onChange={(e) => setV((s) => ({ ...s, closing: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="2.50"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="qd-cx" tip="CapEx dollars funded at close.">
                  CapEx at closing
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="qd-cx"
                    type="text"
                    inputMode="decimal"
                    value={v.capex}
                    onChange={(e) => setV((s) => ({ ...s, capex: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={`${SITE_GLASS_CARD} p-4 md:p-6`}>
            <PanelTitle>Financing terms</PanelTitle>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="qd-r" tip="Annual interest rate on the loan." required>
                  Interest rate
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="qd-r"
                    type="text"
                    inputMode="decimal"
                    value={v.rate}
                    onChange={(e) => setV((s) => ({ ...s, rate: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="5.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="qd-t" tip="Amortization term in years." required>
                  Loan term
                </FieldLabel>
                <input
                  id="qd-t"
                  type="text"
                  inputMode="decimal"
                  value={v.term}
                  onChange={(e) => setV((s) => ({ ...s, term: e.target.value }))}
                  className={toolInputClass}
                  placeholder="30"
                />
              </div>
            </div>
          </div>
          <div className={`${SITE_GLASS_CARD} p-4 md:p-6`}>
            <PanelTitle>Income &amp; expenses (annual)</PanelTitle>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="qd-rent" tip="Scheduled rent before vacancy." required>
                  Annual rental income
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="qd-rent"
                    type="text"
                    inputMode="decimal"
                    value={v.rental}
                    onChange={(e) => setV((s) => ({ ...s, rental: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="qd-oth" tip="Other annual income.">
                  Annual other income
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="qd-oth"
                    type="text"
                    inputMode="decimal"
                    value={v.other}
                    onChange={(e) => setV((s) => ({ ...s, other: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="qd-op" tip="T-12 or pro forma operating expenses." required>
                  Annual operating expenses
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="qd-op"
                    type="text"
                    inputMode="decimal"
                    value={v.opex}
                    onChange={(e) => setV((s) => ({ ...s, opex: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="qd-v" tip="Economic vacancy on rental income." required>
                  Vacancy rate
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="qd-v"
                    type="text"
                    inputMode="decimal"
                    value={v.vacancy}
                    onChange={(e) => setV((s) => ({ ...s, vacancy: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="5.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="qd-res" tip="Replacement reserve $ per unit per year.">
                  CapEx reserve / unit
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="qd-res"
                    type="text"
                    inputMode="decimal"
                    value={v.reserve}
                    onChange={(e) => setV((s) => ({ ...s, reserve: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="250.00"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`${SITE_GLASS_CARD} flex flex-col p-4 md:p-6`}>
          <PanelTitle>Deal analysis results</PanelTitle>
          {out === null ? (
            <ResultsEmpty message="Enter property details to analyze the deal." />
          ) : (
            <div className="mt-4 flex flex-1 flex-col gap-4 text-sm">
              <div className="grid grid-cols-2 gap-3 border-b border-white/10 pb-3">
                <span className="text-[var(--home-muted-on-dark)]">Loan amount</span>
                <span className="text-right font-semibold text-[var(--home-text-on-dark)]">{fmtUsd(out.loan)}</span>
                <span className="text-[var(--home-muted-on-dark)]">Total cash invested</span>
                <span className="text-right font-semibold text-[var(--home-text-on-dark)]">{fmtUsd(out.totalCash)}</span>
                <span className="text-[var(--home-muted-on-dark)]">Effective gross income</span>
                <span className="text-right font-semibold text-[var(--home-text-on-dark)]">{fmtUsd(out.egi)}</span>
                <span className="text-[var(--home-muted-on-dark)]">NOI (after reserves)</span>
                <span className="text-right font-semibold text-[var(--home-text-on-dark)]">{fmtUsd(out.noi)}</span>
                <span className="text-[var(--home-muted-on-dark)]">Annual debt service</span>
                <span className="text-right font-semibold text-rose-300">{fmtUsd(out.annualDebt)}</span>
              </div>
              <div className="rounded-xl bg-white/[0.04] px-3 py-3">
                <p className="text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">Annual cash flow</p>
                <p className={`font-display text-2xl font-bold ${out.cf >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {fmtUsd(out.cf)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <p className="text-[0.65rem] uppercase text-[var(--home-muted-on-dark)]">Going-in cap</p>
                  <p className="font-display text-lg font-bold">{fmtPct(out.cap)}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <p className="text-[0.65rem] uppercase text-[var(--home-muted-on-dark)]">Cash-on-cash</p>
                  <p className="font-display text-lg font-bold">
                    {out.coc === null ? '—' : fmtPct(out.coc)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => setV(DEFAULTS)}
        className="w-full rounded-2xl border border-white/15 bg-white/[0.04] py-3.5 font-body text-sm font-medium text-[var(--home-text-on-dark)] transition hover:bg-white/[0.08]"
      >
        Reset calculator
      </button>
    </div>
  );
}
